export interface Tag {
  id: string;
  name: string;
  color: string;
  description?: string;
  createdByEmail: string;
  isArchived: boolean;
  creationDatetime: string;
  updateDatetime: string;
}

export interface UserTag {
  id: string;
  userId: string;
  tagId: string;
  appliedByEmail: string;
  appliedAt: string;
  note?: string;
  isArchived: boolean;
}

export interface UserTagWithDetails extends UserTag {
  tag: Tag;
}

export const TAG_COLORS = [
  { name: "Red", hex: "#ef4444" }, // Modern red - softer, less harsh
  { name: "Pink", hex: "#ec4899" }, // Modern pink - vibrant but balanced
  { name: "Purple", hex: "#a855f7" }, // Modern purple - less saturated
  { name: "Blue", hex: "#3b82f6" }, // Modern blue - clean and professional
  { name: "Cyan", hex: "#06b6d4" }, // Modern cyan - bright but refined
  { name: "Teal", hex: "#14b8a6" }, // Modern teal - fresh and modern
  { name: "Green", hex: "#22c55e" }, // Modern green - vibrant success color
  { name: "Lime", hex: "#84cc16" }, // Modern lime - less harsh yellow-green
  { name: "Amber", hex: "#f59e0b" }, // Modern amber - warmer than yellow
  { name: "Orange", hex: "#f97316" }, // Modern orange - energetic
  { name: "Slate", hex: "#64748b" }, // Modern slate - sophisticated neutral
  { name: "Indigo", hex: "#6366f1" }, // Modern indigo - distinct from blue
] as const;

export const getLuminanceWeight = (r: number, g: number, b: number): number =>
  0.2126 * r + 0.7152 * g + 0.0722 * b;

const srgbChannelToLinear = (channel: number): number => {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

export const getRelativeLuminance = (r: number, g: number, b: number): number =>
  getLuminanceWeight(
    srgbChannelToLinear(r),
    srgbChannelToLinear(g),
    srgbChannelToLinear(b)
  );

export const getContrastRatio = (
  rgb1: [number, number, number],
  rgb2: [number, number, number]
): number => {
  const l1 = getRelativeLuminance(...rgb1);
  const l2 = getRelativeLuminance(...rgb2);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

export const hslTokenToRgb = (token: string): [number, number, number] => {
  const match = token
    .trim()
    .match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/);
  if (!match) {
    throw new Error(`Invalid HSL token: ${token}`);
  }
  const h = Number(match[1]);
  const s = Number(match[2]) / 100;
  const l = Number(match[3]) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
};

export const getContrastColor = (hexColor: string): string => {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Use WCAG contrast ratio formula for better accuracy
  const luminance = getLuminanceWeight(r, g, b) / 255;

  // For vibrant/saturated colors, prefer white text
  // Only use black text for very light colors (luminance > 0.6)
  return luminance > 0.6 ? "#000000" : "#ffffff";
};
